// src/components/layout/HighlightsStrip.jsx
import React, { useState, useEffect } from "react";
import "../../styles/highlights.css";

export default function HighlightsStrip({ college }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const highlights = college.highlights || [
    { icon: "🏆", label: "NAAC Grade", value: college.naacGrade || "A+" },
    { icon: "📊", label: "NIRF Rank", value: college.nirfRank || "#50" },
    { icon: "👥", label: "Students", value: college.totalStudents || "10k+" },
    { icon: "📚", label: "Programs", value: college.totalPrograms || "100+" },
    { icon: "💼", label: "Placement", value: college.placementRate || "95%" },
    { icon: "🏢", label: "Recruiters", value: college.totalRecruiters || "500+" },
  ];

  // ✅ FIX: Encode filename for URL compatibility
  const encodeImagePath = (path) => {
    if (!path || path.startsWith('http')) return path; // Don't encode external URLs
    
    const parts = path.split('/');
    const filename = parts[parts.length - 1];
    
    // Only encode if filename has spaces or special chars
    if (filename.includes(' ') || filename.includes('(') || filename.includes(')')) {
      parts[parts.length - 1] = encodeURIComponent(filename);
    }
    
    return parts.join('/');
  };

  // ✅ FIX: Helper function to normalize image paths
  const normalizeImagePath = (imagePath) => {
    if (!imagePath) return '';
    
    let normalizedPath = imagePath;
    
    // Add leading slash if needed (not for external URLs)
    if (!imagePath.startsWith('http') && !imagePath.startsWith('/')) {
      normalizedPath = `/${imagePath}`;
    }
    
    // Encode filename part
    return encodeImagePath(normalizedPath);
  };

  // 1. Unsplash Fallback Images (Premium Campus Shots)
  const fallbackImages = [
    "https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=1000&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=1000&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1592280771884-f36b2ae2b00b?q=80&w=1000&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=1000&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?q=80&w=1000&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1519452575417-564c1401ecc0?q=80&w=1000&auto=format&fit=crop"
  ];

  // 2. Get all available images and normalize paths
  const allImages = college.galleryImages && college.galleryImages.length > 0 
    ? college.galleryImages.map(img => normalizeImagePath(img)) // ✅ Normalize all paths
    : fallbackImages;

  // Debug log
  useEffect(() => {
    console.log("HighlightsStrip Images:", allImages.slice(0, 3));
  }, [allImages]);

  // 3. Auto-advance slideshow every 4 seconds
  useEffect(() => {
    if (allImages.length > 3) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % (allImages.length - 2)
        );
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [allImages.length]);

  // 4. Get 3 consecutive images starting from currentImageIndex
  const displayImages = [
    allImages[currentImageIndex],
    allImages[(currentImageIndex + 1) % allImages.length],
    allImages[(currentImageIndex + 2) % allImages.length]
  ];

  return (
    <section className="highlights-strip">
      <div className="highlights-wrapper">
        
        {/* --- LEFT SIDE: STATS --- */}
        <div className="highlights-left">
          {highlights.map((highlight, idx) => (
            <div key={idx} className="highlight-card">
              <div className="highlight-icon">{highlight.icon}</div>
              <div className="highlight-value">{highlight.value}</div>
              <div className="highlight-label">{highlight.label}</div>
            </div>
          ))}
        </div>

        {/* --- RIGHT SIDE: GALLERY SLIDESHOW --- */}
        <div className="highlights-right">
          {displayImages.map((imgSrc, idx) => (
            <div 
              key={`${currentImageIndex}-${idx}`} 
              className="gallery-img-container swipe-left"
            >
              <img 
                src={imgSrc} 
                alt={`Campus Highlight ${idx + 1}`} 
                className="gallery-img" 
                loading="lazy"
                onError={(e) => {
                  console.error("Failed to load image:", imgSrc);
                  e.target.style.display = 'none'; // Hide broken images
                }}
              />
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
